import React from "react";
import { useNavigate } from "react-router-dom";
import { useData } from "@/context/DataContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarX } from "lucide-react";
import { progressBarClass } from "@/lib/format";

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{title}</h1>
        {subtitle && <p className="text-muted-foreground mt-1 text-sm">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

export function KpiCard({ icon: Icon, label, value, sub, onClick, tint = "indigo", testid }) {
  const tints = {
    indigo: "bg-indigo-50 text-indigo-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    blue: "bg-blue-50 text-blue-600",
    rose: "bg-rose-50 text-rose-600",
    violet: "bg-violet-50 text-violet-600",
  };
  return (
    <div
      onClick={onClick}
      data-testid={testid}
      className={`bg-card rounded-xl border border-border p-5 shadow-sm transition-all ${onClick ? "cursor-pointer hover:shadow-md hover:-translate-y-0.5" : ""}`}
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
          <p className="font-display text-2xl font-bold mt-1.5 truncate">{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
        </div>
        {Icon && <div className={`p-2.5 rounded-lg shrink-0 ${tints[tint]}`}><Icon className="h-5 w-5" /></div>}
      </div>
    </div>
  );
}

export function EventSelector() {
  const { events, selectedEventId, setSelectedEventId } = useData();
  if (!events.length) return null;
  return (
    <Select value={selectedEventId || ""} onValueChange={setSelectedEventId}>
      <SelectTrigger className="w-[240px] bg-card" data-testid="event-selector">
        <SelectValue placeholder="Sélectionner un événement" />
      </SelectTrigger>
      <SelectContent>
        {events.map((e) => (
          <SelectItem key={e.id} value={e.id} data-testid={`event-option-${e.id}`}>
            <span className="inline-flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: e.color || "#8b5cf6" }} />
              {e.name}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function ProgressBar({ pct, className = "" }) {
  const p = Math.min(100, Math.max(0, pct || 0));
  return (
    <div className={`h-2.5 w-full rounded-full bg-slate-100 overflow-hidden ${className}`}>
      <div className={`h-full rounded-full transition-all duration-500 ${progressBarClass(p)}`} style={{ width: `${p}%` }} />
    </div>
  );
}

export function EmptyState({ icon: Icon = CalendarX, title, description, action }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 px-6 bg-card rounded-xl border border-dashed border-border">
      <div className="h-14 w-14 rounded-2xl bg-indigo-50 text-indigo-500 flex items-center justify-center mb-4">
        <Icon className="h-7 w-7" />
      </div>
      <h3 className="font-display text-lg font-semibold">{title}</h3>
      {description && <p className="text-muted-foreground text-sm mt-1 max-w-sm">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function NoEvent() {
  const navigate = useNavigate();
  return (
    <EmptyState
      title="Aucun événement"
      description="Créez votre premier événement pour accéder à ce module."
      action={
        <button onClick={() => navigate("/create-event")} data-testid="empty-create-event" className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold">
          Créer un événement
        </button>
      }
    />
  );
}

export function Card({ className = "", children, ...rest }) {
  return <div className={`bg-card rounded-xl border border-border p-5 shadow-sm ${className}`} {...rest}>{children}</div>;
}
